import { ApolloError, useQuery } from '@apollo/client';
import { useLogEvent } from 'hooks/useLogEvent';
import useConfig from 'hooks/useConfig';
import useDialog from 'hooks/useDialog';
import { useCompanyPlanContext } from 'FeatureModules/Auth/Contexts/CompanyPlanContext';
import { GET_ALL_PARTNER_COMPANIES } from 'graphql/secure/queries/partner.queries';
import get from 'lodash/get';
import { useEffect, useMemo, useState } from 'react';
import { ECustomerPlan } from 'FeatureModules/Auth/types/company.types';
import {
  IBasePartnerCompany,
  IPartnerCompanySummary,
} from 'FeatureModules/Partner/types/partner.types';
import { IGraphQlConnection } from 'types/core.types';
import {
  ArrayParam,
  BooleanParam,
  StringParam,
  useQueryParams,
  withDefault,
} from 'use-query-params';
import { EPartnerHeaderColumnEnum } from '../PartnersTable/partnerHeaderColumns';
import { EFilterType, TFilter, TFilterEvent } from 'Components/Filter/filter.types';

interface IUsePartnersData {
  partners: IPartnerCompanySummary[];
  searchTerm: string | null;
  partnerToDelete: IBasePartnerCompany | undefined;
  loading: boolean;
  error: ApolloError | undefined;
  hasNextPage: boolean;
  filters: TFilter[];
  handlePageEndReached: () => void;
  handleCreatePartner: () => void;
  handleSearchTermChange: (searchTerm: string) => void;
  clearDeletingPartner: () => void;
  onDeletePartner: (partner: IBasePartnerCompany) => void;
  onEditPartner: (partner: IBasePartnerCompany, lang?: string) => void;
  refetch: () => void;
  handleFilterChange: (event: TFilterEvent) => void;
  handleFilterReset: () => void;
}

interface IProps {
  customSearch?: string;
  useInfiniteScroll?: boolean;
  useSearchQuery?: boolean;
  skip?: boolean;
}

const usePartners = ({
  customSearch,
  useInfiniteScroll,
  useSearchQuery,
  skip = false,
}: IProps = {}): IUsePartnersData => {
  const { appQueryParams } = useConfig();

  const [partnerToDelete, setPartnerToDelete] = useState<IBasePartnerCompany>();

  const [query, setQuery] = useQueryParams(
    {
      [appQueryParams.new]: withDefault(BooleanParam, false),
      [appQueryParams.query]: withDefault(StringParam, ''),
      [appQueryParams.order]: withDefault(StringParam, 'DESC'),
      [appQueryParams.orderBy]: withDefault(
        StringParam,
        EPartnerHeaderColumnEnum.MODIFIED_TIMESTAMP
      ),
      countriesOfBusiness: withDefault(ArrayParam, undefined),
    },
    { removeDefaultsFromUrl: true }
  );

  const addNewPartner = query[appQueryParams.new];
  const searchTerm = (query[appQueryParams.query] as string) || '';

  const orderByMethod = query[appQueryParams.order] as 'ASC' | 'DESC' | undefined;
  const orderByField = query[appQueryParams.orderBy];
  const countriesOfBusiness = query.countriesOfBusiness;

  const { logEvent } = useLogEvent();
  const { openDialog } = useDialog();
  const { plan } = useCompanyPlanContext();

  const isFreemium = plan === ECustomerPlan.FREEMIUM;

  const { data, loading, error, refetch, fetchMore } = useQuery<{
    partners: IGraphQlConnection<IPartnerCompanySummary>;
  }>(GET_ALL_PARTNER_COMPANIES, {
    variables: {
      filters: {
        searchTerm: useSearchQuery && searchTerm?.length ? searchTerm : undefined,
        partnerRole: 'SUPPLIER', // For now we only show suppliers
        ...(countriesOfBusiness &&
          countriesOfBusiness.length > 0 && { countriesOfBusiness: countriesOfBusiness }),
      },
      orderBy: {
        orderByField,
        orderByMethod,
      },
      ...(useInfiniteScroll && { first: 15 }),
    },
    skip,
  });

  const hasNextPage: boolean = !!data?.partners?.pageInfo?.hasNextPage;
  const endCursor = data?.partners?.pageInfo?.endCursor;

  const partners: IPartnerCompanySummary[] = useMemo(() => {
    if (!data) return [];
    return data?.partners.edges.map(edge => edge.node);
  }, [data]);

  const filteredPartners = useMemo(() => {
    const searchToUse = customSearch !== undefined ? customSearch : searchTerm;
    if (useSearchQuery || !searchToUse?.length) return partners;

    return partners?.filter(partner => {
      // Filter a list of fields to see which fields have a match with the searchTerm
      return ['title', 'location.name'].some(field =>
        // See if the field includes the search term
        get(partner, field)?.toLowerCase().includes(searchToUse.toLowerCase())
      );
    });
  }, [partners, searchTerm, customSearch, useSearchQuery]);

  const handlePageEndReached = () => {
    if (endCursor) {
      fetchMore({
        variables: {
          after: endCursor,
        },
      });
    }
  };

  const filters: TFilter[] = useMemo(
    () => [
      {
        type: EFilterType.COUNTRIES,
        value: (countriesOfBusiness?.filter(Boolean) || undefined) as string[] | undefined,
        label: 'Countries of Business',
        multiple: true,
      },
    ],
    [countriesOfBusiness]
  );

  const handleFilterChange = (event: TFilterEvent) => {
    if (event.type === EFilterType.COUNTRIES) {
      setQuery({
        countriesOfBusiness: event.value ? event.value.filter(Boolean) : undefined,
      });
    }
    logEvent('PARTNERS_FILTERED', { type: event.type });
  };

  const handleFilterReset = () => {
    setQuery({
      countriesOfBusiness: undefined,
    });
  };

  const handleCreatePartner = () => {
    logEvent('CLICK_PARTNER_CREATE');

    if (!isFreemium) {
      openDialog({
        type: 'PARTNER_SELECT_DECISION',
      });
    } else {
      openDialog({
        type: 'ADD_EDIT_PARTNER',
      });
    }
  };

  const clearDeletingPartner = () => {
    setPartnerToDelete(undefined);
  };

  const onDeletePartner = (partner: IBasePartnerCompany) => {
    setPartnerToDelete(partner);
  };

  const onEditPartner = (partner: IBasePartnerCompany) => {
    openDialog({ type: 'ADD_EDIT_PARTNER', props: { partner } });
  };

  const handleSearchTermChange = (term: string) => {
    setQuery({ [appQueryParams.query]: term });
    logEvent('PARTNER_SEARCHED', { searchTerm: term });
  };

  useEffect(() => {
    if (addNewPartner) {
      handleCreatePartner();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [addNewPartner]);

  return {
    partners: filteredPartners,
    searchTerm,
    hasNextPage,
    loading,
    error,
    partnerToDelete,
    filters,
    handleFilterReset,
    handleFilterChange,
    handleCreatePartner,
    handleSearchTermChange,
    clearDeletingPartner,
    onDeletePartner,
    onEditPartner,
    handlePageEndReached,
    refetch,
  };
};

export default usePartners;
